import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { fetchMyOrders } from '../../lib/supabase/api/fetch-my-orders';
import { markOrderPickedUp } from '../../lib/supabase/api/mark-order-picked-up';
import { getUserPhoneNumber, updateUserPhoneNumber } from '../../lib/supabase/api/user-phone';
import { createSSEConnection } from '../../lib/supabase/client/apiClient';
import { supabase } from '../../lib/supabase/client/supabaseClient';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { CheckCircle, Clock, AlertCircle, XCircle, Printer, Phone, MessageSquare } from 'lucide-react';
import ShopMap from './ShopMap';

// Utility function to truncate text
const truncateText = (text: string, maxLength: number) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

const MyOrders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState<string>('');
  const [countryCode, setCountryCode] = useState<string>('+91');
  const [isSavingWhatsapp, setIsSavingWhatsapp] = useState(false);
  const [showWhatsappDialog, setShowWhatsappDialog] = useState(false);

  const supportNumber = process.env.REACT_APP_WHATSAPP_SUPPORT_NUMBER;
  if (!supportNumber) {
    console.error("Support number is not defined in the environment variables.");
  }

  // Helper function to get status configuration
  const getStatusConfig = (status: string) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'received':
      case 'pending':
        return {
          icon: Clock,
          color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
          progress: 10,
          description: 'Order received, waiting to be processed',
          estimatedTime: '2-5 minutes'
        };
      case 'queued':
        return {
          icon: Clock,
          color: 'bg-orange-100 text-orange-800 border-orange-200',
          progress: 25,
          description: 'Order queued for printing',
          estimatedTime: '5-15 minutes'
        };
      case 'printing':
        return {
          icon: Printer,
          color: 'bg-blue-100 text-blue-800 border-blue-200',
          progress: 75,
          description: 'Your document is being printed',
          estimatedTime: '2-10 minutes'
        };
      case 'completed':
        return {
          icon: CheckCircle,
          color: 'bg-green-100 text-green-800 border-green-200',
          progress: 100,
          description: 'Order completed successfully',
          estimatedTime: 'Ready for pickup'
        };
      case 'failed':
        return {
          icon: XCircle,
          color: 'bg-red-100 text-red-800 border-red-200',
          progress: 0,
          description: 'Order failed. Please contact support.',
          estimatedTime: 'Contact support'
        };
      case 'cancelled':
        return {
          icon: AlertCircle,
          color: 'bg-gray-100 text-gray-800 border-gray-200',
          progress: 0,
          description: 'Order was cancelled',
          estimatedTime: 'N/A'
        };
      case 'picked-up':
      case 'picked_up':
        return {
          icon: CheckCircle,
          color: 'bg-purple-100 text-purple-800 border-purple-200',
          progress: 100,
          description: 'Order picked up successfully',
          estimatedTime: 'Collected'
        };
      default:
        return {
          icon: Clock,
          color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
          progress: 10,
          description: 'Order received, waiting to be processed',
          estimatedTime: '2-5 minutes'
        };
    }
  };

  // Load user's WhatsApp number
  const loadWhatsappNumber = async () => {
    const phoneNumber = await getUserPhoneNumber();
    if (phoneNumber) {
      // Parse country code and number (handles both "+91 1234567890" and "+911234567890" formats)
      const match = phoneNumber.match(/^(\+\d{1,4})(.+)$/);
      if (match) {
        setCountryCode(match[1]);
        setWhatsappNumber(match[2].trim()); // Trim any spaces
      } else {
        // Fallback: if no + symbol, assume it's just the number
        setWhatsappNumber(phoneNumber.trim());
      }
    }
  };

  // Mark order as picked up
  const handleMarkAsPickedUp = async (orderId: string) => {
    const success = await markOrderPickedUp(orderId);
    
    if (success) {
      // Update local state
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === orderId ? { ...order, status: 'picked-up' } : order
        )
      );
    } else {
      alert('Failed to mark order as picked up. Please try again.');
    }
  };

  // Save WhatsApp number
  const saveWhatsappNumber = async () => {
    if (!whatsappNumber.trim()) return;

    setIsSavingWhatsapp(true);
    // Save without space for WhatsApp API compatibility
    const fullNumber = `${countryCode}${whatsappNumber.trim()}`;
    const success = await updateUserPhoneNumber(fullNumber);
    
    if (success) {
      setShowWhatsappDialog(false);
    } else {
      alert('Failed to save WhatsApp number. Please try again.');
    }
    
    setIsSavingWhatsapp(false);
  };
  useEffect(() => {
    let sse: EventSource | null = null;

    const initializeOrdersAndSSE = async () => {
        // Fetch initial orders
        setIsLoading(true);
        const orderData = await fetchMyOrders();
        console.log("Fetched orders:", orderData);
        setOrders(orderData);
        
        // Load WhatsApp number
        await loadWhatsappNumber();
        
        try {
        // Establish SSE connection
        sse = await createSSEConnection("/jobs/updates");

        // Handle SSE events
        sse.onopen = () => {
          console.log("SSE connection established");
        };

        sse.addEventListener("job_update", (event) => {
          const update = JSON.parse(event.data);
          console.log("Received job update:", update);

          // Update the orders state with the new status
          setOrders((prevOrders) =>
            prevOrders.map((order) =>
              order.id === update.jobId ? { ...order, status: update.status } : order
            )
          );
        });

        sse.onerror = (err) => {
          console.error("SSE error:", err);
          sse?.close();
        };
      } catch (err) {
        console.error("Error initializing orders or SSE:", err);
      } finally {
        setIsLoading(false);
        setOrders(orderData);
      }
    };

    initializeOrdersAndSSE();

    // Cleanup SSE connection on unmount
    return () => {
      if (sse) {
        sse.close();
        console.log("SSE connection closed");
      }
    };
  }, []);

  if (isLoading) {
    return <div className="text-center py-8">Loading your orders...</div>;
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="text-2xl font-semibold">My Orders</h2>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowWhatsappDialog(true)}
            className="text-green-600 hover:bg-green-50 border-green-200 w-full sm:w-auto"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            {whatsappNumber ? 'Update WhatsApp' : 'Add WhatsApp'}
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setIsDialogOpen(true)}
            className="text-blue-600 hover:bg-blue-50 w-full sm:w-auto"
          >
            <Phone className="w-4 h-4 mr-2" />
            Support
          </Button>
        </div>
      </div>
      
      {/* WhatsApp Number Dialog */}
      <Dialog open={showWhatsappDialog} onOpenChange={(open) => {
        if (!open) setShowWhatsappDialog(false);
      }}>
        <DialogContent 
          className="w-[90%] max-w-md mx-auto p-4 sm:p-6 max-h-[90vh] overflow-y-auto rounded-lg"
        >
          <DialogHeader>
            <DialogTitle className="text-center sm:text-left">WhatsApp Notifications</DialogTitle>
            <DialogDescription className="text-center sm:text-left">
              Add your WhatsApp number to receive order status updates and completion notifications.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 sm:py-4">
            <div className="space-y-3">
              <Label htmlFor="whatsapp">WhatsApp Number</Label>
              <div className="flex gap-2">
                <select
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white text-sm w-24"
                >
                  <option value="+91">🇮🇳 +91</option>
                  <option value="+1">🇺🇸 +1</option>
                  <option value="+44">🇬🇧 +44</option>
                  <option value="+61">🇦🇺 +61</option>
                  <option value="+971">🇦🇪 +971</option>
                  <option value="+65">🇸🇬 +65</option>
                  <option value="+60">🇲🇾 +60</option>
                  <option value="+81">🇯🇵 +81</option>
                  <option value="+86">🇨🇳 +86</option>
                  <option value="+82">🇰🇷 +82</option>
                  <option value="+49">🇩🇪 +49</option>
                  <option value="+33">🇫🇷 +33</option>
                  <option value="+39">🇮🇹 +39</option>
                  <option value="+34">🇪🇸 +34</option>
                  <option value="+7">🇷🇺 +7</option>
                  <option value="+55">🇧🇷 +55</option>
                  <option value="+27">🇿🇦 +27</option>
                  <option value="+234">🇳🇬 +234</option>
                  <option value="+254">🇰🇪 +254</option>
                </select>
                <Input
                  id="whatsapp"
                  type="tel"
                  placeholder="Enter phone number"
                  value={whatsappNumber}
                  onChange={(e) => setWhatsappNumber(e.target.value)}
                  className="flex-1"
                />
              </div>
              <p className="text-sm text-gray-500">
                Example: 9876543210
              </p>
            </div>
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
            <Button 
              onClick={() => setShowWhatsappDialog(false)}
              variant="outline"
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button 
              onClick={saveWhatsappNumber}
              disabled={isSavingWhatsapp || !whatsappNumber.trim()}
              className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
            >
              {isSavingWhatsapp ? 'Saving...' : 'Save Number'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Help Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        if (!open) setIsDialogOpen(false);
      }}>
        <DialogContent 
          className="w-[90%] max-w-md mx-auto p-4 sm:p-6 max-h-[90vh] overflow-y-auto rounded-lg" 
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-center sm:text-left">Need Help With Your Order?</DialogTitle>
            <DialogDescription className="text-center sm:text-left">
              Our support team is ready to assist you with any issues related to your order.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 sm:py-4">
            <p className="mb-3 sm:mb-4 text-sm sm:text-base">
              Contact us via WhatsApp for assistance with:
            </p>
            <ul className="list-disc pl-5 mb-4 text-sm sm:text-base">
              <li>Order status inquiries</li>
              <li>Print quality issues</li>
              <li>Shop related issues</li>
              <li>Payment concerns</li>
            </ul>
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
            <Button 
              onClick={() => {
                window.open(`https://wa.me/${supportNumber}?text=Hello%20CTRLP%20Team%2C%20I%20need%20your%20help%20with%20my%20order`, '_blank');
                setIsDialogOpen(false);
              }}
              className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
            >
              Contact on WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        {orders.length > 0 ? (
        <div className="space-y-4">
          {orders.map((order, index) => {
            const statusConfig = getStatusConfig(order.status);
            const StatusIcon = statusConfig.icon;
            
            return (
              <Card 
                key={order.id || `${order.file_name}-${order.date}-${index}`} 
                className="overflow-hidden transition-all duration-200 hover:shadow-md"
              >
                <CardHeader className="pb-3 bg-gray-50">
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
                    <div className="flex items-start gap-3 w-full sm:w-auto">
                      <StatusIcon className={`w-6 h-6 mt-1 flex-shrink-0 ${
                        order.status.toLowerCase() === 'completed' ? 'text-green-600' :
                        order.status.toLowerCase() === 'failed' ? 'text-red-600' :
                        order.status.toLowerCase() === 'printing' ? 'text-blue-600' :
                        'text-yellow-600'
                      }`} />
                      <div className="min-w-0 flex-1">
                        <CardTitle className="text-lg truncate" title={order.file_name}>
                          {order.file_name}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {new Date(order.date).toLocaleDateString()} • {new Date(order.date).toLocaleTimeString()}
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                  
                  {/* Progress Bar - Hide for picked-up orders */}
                  {order.status.toLowerCase() !== 'picked-up' && order.status.toLowerCase() !== 'picked_up' && (
                    <div className="mt-3">
                      <div className="flex flex-col sm:flex-row justify-between text-sm text-gray-600 mb-1 gap-1">
                        <span className="text-left">{statusConfig.description}</span>
                        <span className="text-left sm:text-right">{statusConfig.estimatedTime}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-500 ${
                            order.status.toLowerCase() === 'completed' ? 'bg-green-500' :
                            order.status.toLowerCase() === 'failed' ? 'bg-red-500' :
                            order.status.toLowerCase() === 'printing' ? 'bg-blue-500' :
                            'bg-yellow-500'
                          }`}
                          style={{ width: `${statusConfig.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </CardHeader>
                
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="col-span-2">
                      <p className="text-gray-600 text-xs">Print Shop</p>
                      <p className="font-medium">{order.shop}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-xs">Amount</p>
                      <p className="font-medium">{order.total}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 text-xs">Pages × Copies</p>
                      <p className="font-medium">{order.pages || 1} × {order.copies || 1}</p>
                    </div>
                  </div>
                </CardContent>

                {/* Shop Location Map - Only show for non-picked-up orders */}
                {order.shop_lat && order.shop_lon && order.status.toLowerCase() !== 'picked-up' && order.status.toLowerCase() !== 'picked_up' && (
                  <div className="px-6 pb-4">
                    <ShopMap
                      shopName={order.shop}
                      latitude={order.shop_lat}
                      longitude={order.shop_lon}
                      address={order.shop_address}
                      className="mt-4"
                    />
                  </div>
                )}

                <CardFooter className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-gray-50">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      if (order.email_invoice_path) {
                        window.open(order.email_invoice_path, '_blank');
                      }
                    }}
                    disabled={!order.email_invoice_path}
                    className="w-full sm:w-auto"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Receipt
                  </Button>
                  
                  {order.status.toLowerCase() === 'completed' && (
                    <Button
                      size="sm"
                      onClick={() => handleMarkAsPickedUp(order.id)}
                      className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark as Picked Up
                    </Button>
                  )}
                  
                  {(order.status.toLowerCase() === 'picked-up' || order.status.toLowerCase() === 'picked_up') && (
                    <Button
                      size="sm"
                      disabled
                      className="bg-purple-100 text-purple-800 hover:bg-purple-100 w-full sm:w-auto cursor-default"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Picked Up
                    </Button>
                  )}
                </CardFooter>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="p-6 sm:p-8 text-center">
          <p className="text-gray-500 mb-4">You haven't placed any orders yet.</p>
          <Button onClick={() => navigate('/get-print')} className="w-full sm:w-auto">Start Printing</Button>
        </Card>
      )}
    </div>
  );
};

export default MyOrders;
